import map from 'lodash/map';
import merge from 'lodash/merge';

import { clientSchema } from '@/features/Clients/Schemas/Base';
import {
  type ClientResource,
  type ClientResourceContainer,
  type ClientResourceKeys,
  isClientResourceKey,
  OPTIONAL_ROLE,
  type Role,
  ROLE,
} from '@/features/Clients/types';
import { isDate, parseDate } from '@/utils/dates/dates';
import {
  ENTITY_TYPE,
  EntityRequestData,
  EntityResourceContainer,
} from '@/features/EntityManager/types';
import { useRefCounter } from '@/features/EntityManager/useRefCounter';

export const getDefaultClientResource = (): ClientResource =>
  clientSchema.getDefault() as ClientResource;

export const createClientResourceContainer = (
  roles?: Role[],
  client?: ClientResource,
  isDirty = false,
): ClientResourceContainer => {
  let newClient = getDefaultClientResource();

  if (client) {
    newClient = merge(newClient, client);
  }

  return {
    refId: useRefCounter().getNextRef(),
    type: ENTITY_TYPE.CLIENT,
    isDirty,
    data: newClient,
    isLoading: false,
    roles: roles || [
      ROLE.VERSICHERUNGSNEHMER,
      ROLE.VERSICHERTE_PERSON,
      ROLE.ZAHLPERSON,
    ],
  };
};

export const buildClientFromApiResponse = (
  apiClient: ClientResource,
): ClientResource => {
  const defaultClient = getDefaultClientResource();
  const client: Record<string, unknown> = {
    ...defaultClient,
  };

  map(Object.keys(defaultClient), (key: ClientResourceKeys) => {
    if (isClientResourceKey(key) && apiClient[key] !== undefined) {
      if (key === 'bankverbindung' || key === 'adresse') {
        map(Object.keys(defaultClient[key]), (subKey) => {
          (client[key] as Record<string, unknown>)[subKey] = (
            apiClient[key] as Record<string, unknown>
          )[subKey];
        });
      } else if (key === 'geburtsdatum' || key === 'fuehrerscheindatum') {
        if (isDate(apiClient[key]) || apiClient[key] === null) {
          client[key] = apiClient[key];
        } else {
          client[key] = parseDate(apiClient[key] as unknown as string);
        }
      }
      // ignore null fields from API and keep values from default client
      else if (apiClient[key] !== null) {
        client[key] = apiClient[key];
      }
    }
  });

  return client as ClientResource;
};

export const transformEntitiesToComparableStructure = (
  entities: EntityRequestData<EntityResourceContainer>,
): Record<Role, undefined | ClientResourceContainer['data']> => {
  const clients: ReturnType<typeof transformEntitiesToComparableStructure> = {
    [ROLE.VERSICHERUNGSNEHMER]: undefined,
    [ROLE.ZAHLPERSON]: undefined,
    [ROLE.MITVERSICHERTER_PARTNER]: undefined,
    [ROLE.VERSICHERTE_PERSON]: undefined,

    [OPTIONAL_ROLE.VERSICHERTE_PERSON_2]: undefined,
    [OPTIONAL_ROLE.VERSICHERTE_PERSON_3]: undefined,
    [OPTIONAL_ROLE.VERSICHERTE_PERSON_4]: undefined,
    [OPTIONAL_ROLE.VERSICHERTE_PERSON_5]: undefined,
    [OPTIONAL_ROLE.VERSICHERTE_PERSON_6]: undefined,

    [OPTIONAL_ROLE.ABWEICHENDER_HALTER]: undefined,
    [OPTIONAL_ROLE.FAHRER_1]: undefined,
    [OPTIONAL_ROLE.FAHRER_2]: undefined,
    [OPTIONAL_ROLE.FAHRER_3]: undefined,
    [OPTIONAL_ROLE.FAHRER_4]: undefined,
    [OPTIONAL_ROLE.FAHRER_5]: undefined,
  };

  Object.values(entities).forEach((entity) => {
    if (entity.type === ENTITY_TYPE.CLIENT) {
      const clientEntity = entity as ClientResourceContainer;
      clientEntity.roles.forEach((role) => {
        clients[role] = clientEntity.data;
      });
    }
  });

  return clients;
};

const hasPersonData = (client: ClientResource): boolean =>
  [client.vorname, client.nachname, client.firma].some(
    (value) => typeof value === 'string' && value.trim() !== '',
  ) ||
  (client.geburtsdatum !== null &&
    client.geburtsdatum !== undefined &&
    String(client.geburtsdatum) !== '');

const hasFurtherRoles = (entity: ClientResourceContainer): boolean =>
  entity.roles.length > 1;

/**
 * Gespeicherte Angebote können mehrere abweichende Halter enthalten, davon
 * einer leer (VGL-7466). Duplikate entstehen nur als Halter mit genau dieser
 * einen Rolle; Einträge mit weiteren Rollen sind Originale und bleiben
 * unangetastet. Vorrang: weitere Rollen, PW-ID, befüllt, sonst der erste.
 */
export const dedupeAbweichenderHalter = (
  entities: ClientResourceContainer[],
): ClientResourceContainer[] => {
  const halter = entities.filter((entity) =>
    entity.roles.includes(OPTIONAL_ROLE.ABWEICHENDER_HALTER),
  );

  if (halter.length < 2) {
    return entities;
  }

  const keep =
    halter.find(hasFurtherRoles) ??
    halter.find(
      (entity) => entity.data.id !== null && entity.data.id !== undefined,
    ) ??
    halter.find((entity) => hasPersonData(entity.data)) ??
    halter[0];

  return entities.filter(
    (entity) =>
      entity === keep ||
      hasFurtherRoles(entity) ||
      !entity.roles.includes(OPTIONAL_ROLE.ABWEICHENDER_HALTER),
  );
};
